import request from "../shared/http";
import appConfig from "../shared/js/app-config";

class LeadService {
  getServiceId() {
    return appConfig.getServiceId();
  }

  basePath() {
    return `/leads/${this.getServiceId()}`;
  }

  async getPipelineStages() {
    const { data } = await request.get(`${this.basePath()}/pipeline-stages`);
    return Array.isArray(data) ? data : [];
  }

  async createPipelineStage(payload) {
    const { data } = await request.post(`${this.basePath()}/pipeline-stages`, payload);
    return data;
  }

  async updatePipelineStage(stageId, payload) {
    const { data } = await request.put(`${this.basePath()}/pipeline-stages/${stageId}`, payload);
    return data;
  }

  async reorderPipelineStages(stageIds) {
    const { data } = await request.put(`${this.basePath()}/pipeline-stages/reorder`, {
      stage_ids: stageIds,
    });
    return Array.isArray(data) ? data : [];
  }

  async deletePipelineStage(stageId) {
    await request.delete(`${this.basePath()}/pipeline-stages/${stageId}`);
  }

  async getCategories() {
    const { data } = await request.get(`${this.basePath()}/categories`);
    return Array.isArray(data) ? data : [];
  }

  async createCategory(payload) {
    const { data } = await request.post(`${this.basePath()}/categories`, payload);
    return data;
  }

  async updateCategory(categoryId, payload) {
    const { data } = await request.put(`${this.basePath()}/categories/${categoryId}`, payload);
    return data;
  }

  async reorderCategories(categoryIds) {
    const { data } = await request.put(`${this.basePath()}/categories/reorder`, {
      category_ids: categoryIds,
    });
    return Array.isArray(data) ? data : [];
  }

  async deleteCategory(categoryId) {
    await request.delete(`${this.basePath()}/categories/${categoryId}`);
  }

  async getLeads({ search, stageId, categoryId, contactId, status, assigneeAccountId, page = 1, limit = 25 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (stageId) params.set("stage_id", stageId);
    if (categoryId) params.set("category_id", categoryId);
    if (contactId) params.set("contact_id", contactId);
    if (status) params.set("status", status);
    if (assigneeAccountId) params.set("assignee_account_id", assigneeAccountId);
    params.set("page", String(page));
    params.set("limit", String(limit));
    const { data } = await request.get(`${this.basePath()}?${params.toString()}`);
    return data || { leads: [], page: 1, limit: 25, total: 0, totalPages: 1 };
  }

  async getLeadsBoard({ search, status, categoryId, assigneeAccountId } = {}) {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (categoryId) params.set("category_id", categoryId);
    if (assigneeAccountId) params.set("assignee_account_id", assigneeAccountId);
    const { data } = await request.get(`${this.basePath()}/board?${params.toString()}`);
    return data || { columns: [] };
  }

  async getLeadById(leadId) {
    const { data } = await request.get(`${this.basePath()}/${leadId}`);
    return data;
  }

  async getLeadsByContact(contactId) {
    const { data } = await request.get(`${this.basePath()}/contact/${contactId}`);
    return Array.isArray(data) ? data : [];
  }

  async createLead(payload) {
    const { data } = await request.post(this.basePath(), payload);
    return data;
  }

  async updateLead(leadId, payload) {
    const { data } = await request.put(`${this.basePath()}/${leadId}`, payload);
    return data;
  }

  async updateLeadStage(leadId, stageId) {
    const { data } = await request.patch(`${this.basePath()}/${leadId}/stage`, {
      stage_id: stageId,
    });
    return data;
  }

  async deleteLead(leadId) {
    await request.delete(`${this.basePath()}/${leadId}`);
  }

  async createTask(leadId, payload) {
    const { data } = await request.post(`${this.basePath()}/${leadId}/tasks`, payload);
    return data;
  }

  async updateTask(leadId, taskId, payload) {
    const { data } = await request.put(`${this.basePath()}/${leadId}/tasks/${taskId}`, payload);
    return data;
  }

  async deleteTask(leadId, taskId) {
    await request.delete(`${this.basePath()}/${leadId}/tasks/${taskId}`);
  }

  async createComment(leadId, body) {
    const { data } = await request.post(`${this.basePath()}/${leadId}/comments`, { body });
    return data;
  }

  async updateComment(leadId, commentId, body) {
    const { data } = await request.put(`${this.basePath()}/${leadId}/comments/${commentId}`, { body });
    return data;
  }

  async deleteComment(leadId, commentId) {
    await request.delete(`${this.basePath()}/${leadId}/comments/${commentId}`);
  }

  async getReminders(leadId) {
    const { data } = await request.get(`${this.basePath()}/${leadId}/reminders`);
    return Array.isArray(data) ? data : [];
  }

  async createReminder(leadId, payload) {
    const { data } = await request.post(`${this.basePath()}/${leadId}/reminders`, payload);
    return data;
  }

  async updateReminder(leadId, reminderId, payload) {
    const { data } = await request.put(
      `${this.basePath()}/${leadId}/reminders/${reminderId}`,
      payload,
    );
    return data;
  }

  async deleteReminder(leadId, reminderId) {
    await request.delete(`${this.basePath()}/${leadId}/reminders/${reminderId}`);
  }
}

export default new LeadService();
